import { showChoices } from "./script-choices.js";
import { convertScript, sourceScript } from "./script-conversion.js";
import type { ScriptElements } from "./script-elements.js";
import type { WrittenScript } from "./script-decisions.js";
import type { ScriptResources } from "./script-loading.js";
import { conversionNodes } from "./script-output.js";

/**
 * Putting the conversions on the page, and taking them off again.
 *
 * Separate from `script-tool.ts`, which decides when to convert and what to
 * convert with. Everything a conversion writes is written here, in one place,
 * because the panels and the account under them are one answer: a view that
 * filled the panels and forgot the account would leave the page listing the
 * decisions of a conversion it is no longer showing.
 */
export class ScriptView {
  readonly #elements: ScriptElements;

  constructor(elements: ScriptElements) {
    this.#elements = elements;
  }

  #showChoices(written: readonly WrittenScript[]): void {
    const { choicesPanel, choices } = this.#elements;

    if (choicesPanel !== undefined && choices !== undefined) {
      showChoices(choicesPanel, choices, written);
    }
  }

  /**
   * The page with nothing on it, which is what an empty input deserves.
   *
   * An empty input is not an error and the page opens on one, so everything a
   * conversion fills is emptied rather than left showing the last thing that
   * was typed.
   */
  clear(): void {
    for (const panel of this.#elements.panels) {
      panel.output.replaceChildren();
    }

    this.#showChoices([]);
  }

  /**
   * Convert a text into every orthography the page has a panel for.
   *
   * The source script is worked out once and handed to all of them, rather
   * than detected again per panel: it is a fact about the text rather than
   * about the target, and three panels disagreeing about which script they had
   * been given would be a bug with no way to see it.
   */
  show(resources: ScriptResources, text: string): void {
    const from = sourceScript(text, resources.tables);

    const written = this.#elements.panels.map((panel) => {
      const conversion = convertScript(
        resources.dictionary,
        resources.tables,
        text,
        panel.target,
        from,
      );

      panel.output.replaceChildren(...conversionNodes(conversion));

      return { target: panel.target, conversion };
    });

    this.#showChoices(written);
  }
}
